import { Injectable, Injector } from "@angular/core";
import { collection, doc, Firestore, getDoc, onSnapshot } from "@angular/fire/firestore";
import { Data } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { AuthStateService } from "@core/auth-state.service";
import { CatchErrorService } from "@core/catch-error.service";
import { getFunctions, httpsCallable } from "firebase/functions";
import { BehaviorSubject, catchError, EMPTY, from, map, mergeMap, Observable, of, shareReplay, Subject, switchMap, take, throwError, toArray } from "rxjs";
import { BaseService } from "../app.base.service";
import { CustomDatePipe } from "../pipe/custom.datepipe";
import { CommonService } from "./common/common.service";
@Injectable({
  providedIn: "root",
})
export class LuckyNumberService extends BaseService {
  showAvailableLn = new Subject<Boolean>();
  selectedLnSaved = new Subject<Boolean>();
  hideAvailableLn = new Subject<Boolean>();
  hideAvailableGR = new Subject<boolean>();
  selectedGRSaved = new Subject<boolean>();
  loadPrizeDrawCard$ = new BehaviorSubject(false);
  //updateSelectedNum$ = new Subject<any>();

  constructor(
    private firestore: Firestore,
    private injector: Injector,
    public datepipe: CustomDatePipe,
    private catchService: CatchErrorService,
    private authService: AuthService,
    private commonService: CommonService,
    private authStateService: AuthStateService,
  ) {
    super(injector);
  }

  getShowWidgetsData() {
    return this.commonService.getUid("LuckyNumberService").pipe(
      switchMap((userId) => {
        if (!userId) return of(null); // Exit if userId is invalid

        const docRef = doc(
          this.firestore,
          `/users/${userId}/special_features/show_widgets`
        ); // Reference Firestore document

        return this.catchService.isOnline$.pipe(
          switchMap((isOnline) => {
            if (!isOnline) return of(null);

            // Gate on customTokenReady$ before opening the Firestore listener so it
            // never fires before the Firebase custom token is confirmed stored,
            // preventing the permission-denied race condition (LOTTOSOCIAL-PWA-1MED).
            return this.authStateService.customTokenReady$.pipe(
              take(1),
              switchMap(() => new Observable((observer) => {
                const unsubscribe = onSnapshot(
                  docRef,
                  (docSnap) => {
                    observer.next(docSnap?.data() || null);
                  },
                  (error) => {
                    console.error("Error fetching show widgets data:", error);
                    observer.error(null);
                  }
                );

                // Cleanup on unsubscribe
                return () => unsubscribe();
              }))
            );
          }),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError,
          shareReplay(1)
        );
      }),
      shareReplay(1)
    );
  }

  getLuckyNumbersEnabled() {
    return this.commonService.getUid("LuckyNumberService").pipe(
      switchMap((userId) => {
        if (!userId) return of([]); // Exit if userId is invalid

        return this.catchService.isOnline$.pipe(
          switchMap((isOnline) => {
            if (!isOnline) return of([]);

            const docRef = doc(this.firestore, `/users/${userId}/special_features/features`);

            // Gate on customTokenReady$ before opening the Firestore listener so it
            // never fires before the Firebase custom token is confirmed stored,
            // preventing the permission-denied race condition (LOTTOSOCIAL-PWA-1MED).
            return this.authStateService.customTokenReady$.pipe(
              take(1),
              switchMap(() => new Observable((observer) => {
                const unsubscribe = onSnapshot(
                  docRef,
                  (docSnap) => {
                    observer.next(docSnap?.data() || {});
                  },
                  (error) => {
                    console.error("Error fetching Lucky Numbers data:", error);
                    observer.error(null);
                  }
                );

                // Cleanup on unsubscribe
                return () => unsubscribe();
              }))
            );
          }),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError,
          shareReplay(1)
        );
      }),
      shareReplay(1)
    );
  }


  getUserCurrentBalance() {
    return this.commonService.getUid("BalanceService").pipe(
      switchMap((userId) => {
        if (!userId) return of(null); // Exit if userId is invalid

        const docRef = doc(this.firestore, `/users/${userId}/balance/current`);

        return this.catchService.isOnline$.pipe(
          switchMap((isOnline) => {
            if (!isOnline) return of(null);

            // Gate on customTokenReady$ before opening the Firestore listener so it
            // never fires before the Firebase custom token is confirmed stored,
            // preventing the permission-denied race condition (LOTTOSOCIAL-PWA-1MED).
            return this.authStateService.customTokenReady$.pipe(
              take(1),
              switchMap(() => new Observable((observer) => {
                const unsubscribe = onSnapshot(
                  docRef,
                  (docSnap) => observer.next(docSnap?.data() || null),
                  (error) => {
                    console.error("Error fetching user current balance:", error);
                    observer.next(null);
                    observer.complete();
                  }
                );

                // Cleanup when unsubscribed
                return () => unsubscribe();
              }))
            );
          }),
          this.catchService.retryWhen,
          this.catchService.catchOfflineError,
          shareReplay(1)
        );
      }),
      shareReplay(1)
    );
  }

  saveSelectedLuckyNumber(
    syndicateId: any,
    ticketDetails: any,
    selectedLuckyNumbers: any
  ) {
    return this.commonService.getUid("LuckyNumberService").pipe(
      take(1),
      switchMap((userId) => {
        if (!userId) return of(false); // Exit if no userId

        const productAry = ticketDetails?.product;
        if (!syndicateId || !Array.isArray(productAry) || !productAry.length) {
          return of(false);
        }

        return from(productAry).pipe(
          mergeMap(async (eachProduct: any) => {
            try {
              const productId = eachProduct?.id;
              const selectedLuckyNumber = selectedLuckyNumbers[productId];
              if (!selectedLuckyNumber) return;

              const path_id = "IDrvZSfUE06K5vYHtC4l";
              const doc_data = { selected_numbers: selectedLuckyNumber };
              const method = "update";
              const dv = { docId: String(syndicateId), productId: productId };
              await this.authService.getV2CloudFunction("updatefirestoredoc", {
                path_id,
                doc_data,
                dv,
                method
              }).subscribe({
                error: (error) => {
                  console.error("Error updating document:", error);
                  return error;
                }
              });
            } catch (err) {
              console.error("Error updating selected lucky numbers:", err);
            }
          }),
          toArray(), // Wait for all updates to finish
          map(() => true)
        );
      })
    );
  }

  getSelectedNumbersData(
    syndicateId: any,
    selectedLuckyNumbers: any,
    ticketDetails: any
  ) {
    return this.commonService.getUid("LuckyNumberService").pipe(
      take(1),
      switchMap((userId) => {
        if (!userId) return of(false);

        const productAry = ticketDetails?.product;
        if (!syndicateId || !Array.isArray(productAry) || !productAry.length) {
          return of(false);
        }

        return from(productAry).pipe(
          mergeMap(async (eachProduct: any) => {
            try {
              const productId = eachProduct?.id;
              const selectedLuckyNumber = selectedLuckyNumbers[productId];
              if (!selectedLuckyNumber) return;

              const nextDrawDate = eachProduct?.next_draw_date?.toDate();
              const drawSelected = this.datepipe.transform(nextDrawDate, "yyyy-MM-dd");

              const selectedNumbersWithDate = { [drawSelected]: selectedLuckyNumber };
              const selected_lucky_numbers = {
                ...(eachProduct?.selected_numbers || {}),
                ...selectedNumbersWithDate,
              };

              const path_id = "IDrvZSfUE06K5vYHtC4l";
              const doc_data = { selected_numbers: selected_lucky_numbers };
              const dv = { docId: syndicateId, productId: productId };
              const method = "update";

              await this.authService.getV2CloudFunction("updatefirestoredoc", {
                path_id,
                doc_data,
                dv,
                method,
              }).subscribe({
                error: (err) => {
                  console.error('Error in selected_lucky_numbers: ', err);
                  throw err;
                }
              });
            } catch (err) {
              console.error("Error updating selected numbers:", err);
            }
          }),
          toArray(),
          map(() => true)
        );
      })
    );
  }


  updateCurrentBalance(balanceDetails: any) {
    try {
      if (balanceDetails?.hasOwnProperty("selectedLNCount") && balanceDetails?.hasOwnProperty("remainingCount")) {
        const getInUseCount = balanceDetails?.selectedLNCount;
        const getRemainingCount = balanceDetails?.remainingCount;
        const path_id = "SK8mBmn1R6bNDdj1cul8";
        const doc_data = {
              lucky_numbers: {
                in_use: getInUseCount,
                remaining: getRemainingCount,
              },
            };
        const method = "update";
        return from(this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          method
        })).pipe(
          catchError((err) => {
            console.error("Error updating current balance:", err);
            return of(err);
          })
        );
      } else {
        return false; // Return false if required properties are missing
      }
    } catch (error) {
      return error; // Return the error in case of exception
    }
  }
 updateGoldRingCurrentBalance(balanceDetails: Data){
   try {
      if (balanceDetails?.hasOwnProperty("selectedGRCount") && balanceDetails?.hasOwnProperty("remainingCount")) {
        const getInUseCount = balanceDetails?.selectedGRCount;
        const getRemainingCount = balanceDetails?.remainingCount;
        const path_id = "SK8mBmn1R6bNDdj1cul8";
        const doc_data = {
          gold_rings: {
            in_use: getInUseCount,
            remaining: getRemainingCount,
          },
        };
        const method = "update";

        this.authService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          method,
        }).subscribe({
          error: (err) => {
            console.error('Error updating gold ring balance:', err);
          }
        });
      } else {
        return false; // Return false if the required properties are missing
      }
    } catch (error) {
      return error; // Handle unexpected errors
    }
  }


  confirmGoldRingSelection(reqObj: Data) {
    const functions = getFunctions();
    functions.region = AuthService.FIREBASE_REGION;
    return this.authService?.v2CloudFunctionResponse(
      httpsCallable(functions, "updategoldrings")(this.requestBodyService.confirmGoldRingSelection(reqObj))
    );
  }

  getSelectedLNs(syndId: any, productId: any) {
    return this.authStateService.customTokenReady$.pipe(
      take(1),
      switchMap(() =>
        this.commonService.getUid("LottoService").pipe(
          take(1),
          switchMap((userId) => {
            if (!userId) return of(null);

            return this.catchService.isOnline$.pipe(
              switchMap((isOnline) => {
                if (!isOnline) return of(null);

                const userRef = doc(this.firestore, `users/${userId}`);
                const managedTicketsRef = collection(userRef, "managed_tickets");
                const syndicateDocRef = doc(managedTicketsRef, String(syndId));
                const productsRef = collection(syndicateDocRef, "products");
                const productDocRef = doc(productsRef, productId);

                return from(getDoc(productDocRef)).pipe(
                  map((documentSnapshot) => documentSnapshot.exists() ? documentSnapshot.data() : null),
                  catchError((err) => {
                    if ((err as { code?: string })?.code === "permission-denied") {
                      return of(null);
                    }
                    return throwError(() => err);
                  })
                );
              }),
              this.catchService.retryWhen,
              this.catchService.catchOfflineError
            );
          })
        )
      ),
      shareReplay(1)
    );
  }


  getOnboardData(docId: string) {
    return this.catchService.isOnline$.pipe(
      switchMap((isOnline) => {
        if (!isOnline) return EMPTY; // skip emission when offline to avoid null downstream

        const docRef = doc(
          this.firestore,
          'Global_lookup/onboarding/slider_show',
          docId
        );

        return from(getDoc(docRef)).pipe(
          this.catchService.retryWhen,
          this.catchService.catchOfflineError
        );
      }),
      this.catchService.retryWhen,
      this.catchService.catchOfflineError,
      shareReplay(1)
    );
  }
  getLotteryData(product_name: string): Observable<unknown> {
    if (!product_name) {
      return of(null); // Return Observable with null for invalid product name
    }
    
    const lotteryDocRef = doc(this.firestore, `lotteries/${String(product_name)}`); // Reference Firestore document
    return new Observable((observer) => {
      getDoc(lotteryDocRef)
        .then((docSnap) => {
          if (docSnap.exists()) {
            observer.next(docSnap.data()); // Emit document data
          } else {
            observer.next(null); // Emit null if the document doesn't exist
          }
          observer.complete();
        })
        .catch((error) => {
          console.error('Error fetching lottery data:', error);
          observer.next(null); // Emit null on error instead of throwing
          observer.complete();
        });
    });
  }

  isDrawDateExpired(date1: Date, date2: Date, drawCloseTime: any) {
    try {
      if (drawCloseTime !== "") {
        let drawCloseTimeSplit = drawCloseTime.split(":");
        let nextDrawDateCutOff: Date = new Date(
          date1.getFullYear(),
          date1.getMonth(),
          date1.getDate(),
          drawCloseTimeSplit[0],
          drawCloseTimeSplit[1],
          0
        );
        if (nextDrawDateCutOff.getTime() > date2.getTime()) return false;
        else return true;
      } else {
        if (date1.getTime() > date2.getTime()) return false;
        else return true;
      }
    } catch (error) {
      return true;
    }
  }

  async getSelectedNumbersDataCf(syndicate_id: string, selectedNumbers: any) {
    try {
      const userId = await this.commonService.getUid("LottoService").pipe(take(1)).toPromise();
      if (!userId) throw new Error("User ID is invalid");

      const updateSelectedLuckyNumbers = httpsCallable(this.functions, "updateSelectedLuckyNumbers");

      const result = await updateSelectedLuckyNumbers({
        userId,
        syndicateId: syndicate_id,
        selectedLuckyNumbers: selectedNumbers
      });

      return result.data; // Return the function result data
    } catch (error) {
      console.error('Error updating lucky numbers:', error);
      throw error; // Propagate error
    }
  }

}
